import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/ui/Button';
import { Field } from '../../../shared/ui/Field';
import { ApiError, NetworkError, isNetworkError } from '../../../shared/api/client';
import { useAuthStore } from '../model/auth.store';
import {
  hasFieldErrors,
  validateLoginForm,
  type FieldErrors,
} from '../model/auth.validation';

export default function LoginPage() {
  const status = useAuthStore((state) => state.status);
  const actionStatus = useAuthStore((state) => state.actionStatus);
  const loginAction = useAuthStore((state) => state.login);

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/today';

  if (status === 'authenticated') {
    return <Navigate to={from} replace />;
  }

  const pending = actionStatus === 'pending';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const validation = validateLoginForm({ login, password });
    if (hasFieldErrors(validation)) {
      setErrors(validation);
      return;
    }
    setErrors({});

    try {
      await loginAction(login.trim(), password);
      navigate(from, { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
        return;
      }
      if (isNetworkError(error) || error instanceof NetworkError) {
        setFormError('Нет соединения с сервером. Вход и регистрация требуют сети.');
        return;
      }
      setFormError('Не удалось войти. Попробуйте ещё раз.');
    }
  }

  return (
    <div className="tg-page flex min-h-dvh items-center justify-center">
      <div className="w-full max-w-md space-y-6">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-tg-accent">Личный задачник</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-tg-text">Brainote</h1>
          <p className="mt-2 text-sm text-tg-text-secondary">
            Войдите, чтобы работать с задачами офлайн и синхронизировать их между устройствами.
          </p>
        </header>

        <form className="tg-card space-y-4 p-5 sm:p-6" onSubmit={handleSubmit} noValidate>
          <h2 className="text-lg font-semibold text-tg-text">Вход</h2>

          {formError ? (
            <div
              className="rounded-xl border border-tg-danger/40 bg-tg-danger-soft px-3 py-2 text-sm text-tg-danger"
              role="alert"
            >
              {formError}
            </div>
          ) : null}

          <Field
            label="Логин"
            name="login"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={login}
            error={errors.login ?? null}
            onChange={(event) => {
              setLogin(event.target.value);
            }}
          />

          <Field
            label="Пароль"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            error={errors.password ?? null}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />

          <Button type="submit" loading={pending} className="w-full">
            Войти
          </Button>

          <p className="text-center text-sm text-tg-text-secondary">
            Нет аккаунта?{' '}
            <Link className="text-tg-accent hover:text-tg-accent-hover" to="/register">
              Регистрация по коду приглашения
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
